<template>
  <div data-allow-mismatch="text, style">
    <span v-if="props.status === 'TODO' || props.status === 'OPEN'">
      <RevTag v-if="isLate" :label="lateTagLabel" variant="danger" />
      <RevTag v-if="!isLate" :label="todoTagLabel" variant="info" />
    </span>
    <RevTag
      v-if="props.status === 'UPCOMING'"
      :label="upcomingTagLabel"
      variant="secondary"
    />
    <RevTag
      v-if="props.status === 'NO_ACTION_NEEDED'"
      :label="i18n(translations.noActionNeeded)"
      variant="success"
    />
    <RevTag
      v-if="props.status === 'COMPLETED'"
      :label="i18n(translations.completed)"
      variant="success"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { DateTime } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { isPastDate } from '@backmarket/utils/date/isPastDate'
import { RevTag } from '@ds/components/Tag'

import type {
  RequestStatusType,
  TaskStatusType,
  TaskType,
} from '~/api/seller-after-sales/types'
import { useTaskLabels } from '~/scopes/customer-care/pages/customer-care/composables/useTaskLabel'

import translations from './TaskStatus.translations'

const HOUR_IN_MILLISECONDS = 1000 * 60 * 60

const i18n = useI18n()
const logger = useLogger()

const props = withDefaults(
  defineProps<{
    status: RequestStatusType | TaskStatusType
    deadline?: string | null
    taskType?: TaskType | null
  }>(),
  {
    deadline: null,
    taskType: null,
  },
)

const label = useTaskLabels(props.taskType)

const isLate = computed(() => {
  if (props.deadline === null) {
    return false
  }

  return isPastDate(props.deadline)
})
const timeLeft = computed(() => {
  if (props.deadline === null) {
    logger.error(
      'Request deadline is null with status TODO when displaying the task status',
      { owners: ['bot-squad-seller-care-front'] },
    )

    return i18n(translations.todo)
  }

  const hoursLeft =
    (new Date(props.deadline).getTime() - Date.now()) / HOUR_IN_MILLISECONDS

  if (hoursLeft > 24) {
    const daysLeft = Math.ceil(hoursLeft / 24)

    return i18n(translations.daysLeft, { count: daysLeft })
  }

  return i18n(translations.hoursLeft, { count: Math.ceil(hoursLeft) })
})

const todoTagLabel = computed(() => {
  if (props.taskType === null) {
    return timeLeft.value
  }

  return `${label}: ${timeLeft.value}`
})

const timeLate = computed(() => {
  const hoursLate =
    (Date.now() - new Date(props.deadline as DateTime).getTime()) /
    HOUR_IN_MILLISECONDS

  if (hoursLate > 24) {
    const daysLate = Math.ceil(hoursLate / 24)

    return i18n(translations.daysLate, { count: daysLate })
  }

  return i18n(translations.hoursLate, { count: Math.ceil(hoursLate) })
})

const lateTagLabel = computed(() => {
  if (props.taskType === null) {
    return timeLate.value
  }

  return `${label}: ${timeLate.value}`
})

const upcomingTagLabel = computed(() => {
  if (props.taskType === null) {
    return i18n(translations.upcoming)
  }

  return `${label}: ${i18n(translations.upcoming)}`
})
</script>
